import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import { CatalogView } from '@lemoncloud/codes-goods-api';
import { MediaView } from '@lemoncloud/codes-goods-api/dist/modules/medias/views';
import { useStereoIcon } from '@web/app/shared';
import { ImageViewDialog } from '@web/app/shared/components/image-view-dialog';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { AlertCircle, ChevronLeft, RefreshCw } from 'lucide-react';

import { useFetchProductById } from '@codes/product';
import { ImageItem, PdfItem, getCurrencySymbol } from '@codes/react-utils';
import { PdfViewDialog } from '@codes/shared';
import { Avatar, AvatarImage, Button, Input, Label, Skeleton } from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

export const ProductDetailPage = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const getStereoIcon = useStereoIcon();
    const isEnglish = i18n.language === 'en';

    const {
        data: product,
        isFetching,
        isFetched,
        error,
        refetch,
        isRefetching,
    } = useFetchProductById(productId as string);

    const [fileUrl, setfileUrl] = useState('');
    const [imgViewerOpen, setImageViewerOpen] = useState(false);
    const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

    const viewMediaBtnTapped = (media: MediaView) => {
        if (!media) {
            return;
        }

        const isPdf = media.contentType?.includes('pdf');
        setfileUrl(media?.url || '');

        if (!isPdf) {
            setImageViewerOpen(true);
            return;
        }

        setPdfViewerOpen(true);
    };

    const getIconImage = (catalog: CatalogView | undefined) => {
        if (!catalog) {
            return '/assets/icons/empty.svg';
        }

        const thumb = catalog.thumbs && catalog.thumbs.length >= 1 ? catalog.thumbs[0] : null;

        if (thumb) {
            return thumb;
        }

        return getStereoIcon(catalog.stereo as string);
    };

    if (!productId) {
        return (
            <PageContainer maxWidth="lg" className="flex flex-col items-center">
                <div className="w-full max-w-3xl">
                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-text mb-2">{t('serviceDetail.empty.title')}</h3>
                            <p className="text-sub">{t('serviceDetail.empty.description')}</p>
                        </div>
                        <Button onClick={() => navigate(-1)} className="gap-2">
                            <ChevronLeft className="w-4 h-4" />
                            {t('common.back')}
                        </Button>
                    </div>
                </div>
            </PageContainer>
        );
    }

    if (isFetching || isRefetching) {
        return (
            <PageContainer maxWidth="lg" className="flex flex-col items-center">
                <div className="w-full max-w-3xl">
                    <div className="flex items-center gap-2 mb-8">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <Skeleton className="h-6 w-48" />
                    </div>

                    <div className="space-y-7">
                        <div className="space-y-3">
                            <Skeleton className="h-5 w-32" />
                            <Skeleton className="h-4 w-48" />
                        </div>

                        <div className="flex gap-2">
                            <Skeleton className="w-10 h-10 rounded-full" />
                            <div className="flex-1 space-y-2">
                                <Skeleton className="h-5 w-40" />
                                <Skeleton className="h-4 w-60" />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <Skeleton className="h-5 w-36" />
                            <div className="bg-box p-4 rounded-lg space-y-3">
                                <Skeleton className="h-4 w-24" />
                                <Skeleton className="h-16 w-full" />
                            </div>
                        </div>

                        <div className="space-y-4">
                            <Skeleton className="h-5 w-40" />
                            <div className="bg-box p-4 rounded-lg space-y-3">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex justify-between">
                                        <Skeleton className="h-4 w-24" />
                                        <Skeleton className="h-4 w-16" />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <Skeleton className="h-5 w-44" />
                            <Skeleton className="h-10 w-full" />
                        </div>
                    </div>
                </div>
            </PageContainer>
        );
    }

    if (error) {
        return (
            <PageContainer maxWidth="lg" className="flex flex-col items-center">
                <div className="w-full max-w-3xl">
                    <div className="flex items-center gap-2 mb-8">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="text-xl font-semibold">{t('serviceDetail.title')}</div>
                    </div>

                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="w-16 h-16 rounded-full bg-error/15 flex items-center justify-center">
                            <AlertCircle className="w-8 h-8 text-error" />
                        </div>
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-text mb-2">
                                {t('serviceDetail.error.loadFailed')}
                            </h3>
                            <p className="text-sub">
                                {error instanceof Error ? error.message : t('serviceDetail.error.unexpected')}
                            </p>
                        </div>
                        <Button onClick={() => refetch()} disabled={isRefetching} className="gap-2">
                            <RefreshCw className={`w-4 h-4 ${isRefetching ? 'animate-spin' : ''}`} />
                            {t('serviceDetail.error.tryAgain')}
                        </Button>
                    </div>
                </div>
            </PageContainer>
        );
    }

    if (!product && isFetched) {
        return (
            <PageContainer maxWidth="lg" className="flex flex-col items-center">
                <div className="w-full max-w-3xl">
                    <div className="flex items-center gap-2 mb-8">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-2">
                            <ChevronLeft className="w-5 h-5" />
                        </Button>
                        <div className="text-xl font-semibold">{t('serviceDetail.title')}</div>
                    </div>

                    <div className="flex flex-col items-center gap-6 py-12">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-text mb-2">{t('serviceDetail.empty.title')}</h3>
                            <p className="text-sub">{t('serviceDetail.empty.description')}</p>
                        </div>
                    </div>
                </div>
            </PageContainer>
        );
    }

    return (
        <>
            <PageContainer maxWidth="lg" className="flex flex-col items-center animate-enter">
                <div className="w-full max-w-3xl">
                    {/* Back + Title */}
                    <div className="flex items-center gap-2 mb-6">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="p-1.5 -ml-2">
                            <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <h1 className="text-lg font-semibold font-display">{t('serviceDetail.title')}</h1>
                    </div>

                    <div className="space-y-6">
                        {/* Service Header Card */}
                        <Card className="p-5">
                            <div className="text-xs text-text-500 uppercase tracking-wider font-medium mb-3">
                                {t('serviceDetail.requestedService')}
                            </div>
                            <div className="text-xs text-text-400 mb-3">
                                {t('serviceDetail.applicationDate')} {formatDate(product.createdAt, 'YYYY. MM. DD')}
                            </div>
                            <div className="flex items-center gap-3">
                                <Avatar className="w-10 h-10 shrink-0">
                                    <AvatarImage src={getIconImage(product.catalog$)} alt={product.catalog$?.stereo} />
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold truncate">{product.name}</div>
                                    {(isEnglish ? product.catalog$?.summaryEn : product.catalog$?.summary) && (
                                        <div className="text-xs text-text-500 mt-0.5 line-clamp-1">
                                            {isEnglish ? product.catalog$.summaryEn : product.catalog$.summary}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card>

                        {/* Service Information Section */}
                        {product.catalog$ &&
                            ((isEnglish ? product.catalog$.descEn : product.catalog$.desc) ||
                                (product.catalog$.dependency$$ && product.catalog$.dependency$$.length > 0)) && (
                                <>
                                    <div className="w-full h-px bg-border"></div>
                                    <div>
                                        <div className="text-xs font-semibold text-text-500 uppercase tracking-wider mb-3">
                                            {t('serviceDetail.serviceInfo')}
                                        </div>

                                        <Card className="bg-box p-4">
                                            <div className="flex flex-col gap-6">
                                                {/* Description */}
                                                {(isEnglish ? product.catalog$.descEn : product.catalog$.desc) && (
                                                    <div>
                                                        <div className="text-foreground font-semibold text-base mb-3">
                                                            {t('serviceDetail.description')}
                                                        </div>
                                                        <div className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                                                            {isEnglish
                                                                ? product.catalog$.descEn
                                                                : product.catalog$.desc}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Dependencies */}
                                                {product.catalog$.dependency$$ &&
                                                    product.catalog$.dependency$$.length > 0 && (
                                                        <div>
                                                            <div className="text-foreground font-semibold text-base mb-4">
                                                                {t('serviceDetail.dependencies')}
                                                                <span className="text-point font-normal text-sm ml-2">
                                                                    ({product.catalog$.dependency$$.length})
                                                                </span>
                                                            </div>
                                                            <div className="flex flex-col gap-3">
                                                                {product.catalog$.dependency$$.map((dep, index) => (
                                                                    <div
                                                                        key={index}
                                                                        className="flex items-center justify-between p-3 bg-background border border-border rounded-lg shadow-sm hover:shadow-md transition-shadow"
                                                                    >
                                                                        <div className="font-medium text-foreground">
                                                                            {dep.name}
                                                                        </div>
                                                                        <div className="text-xs font-medium text-primary-foreground capitalize bg-primary px-3 py-1 rounded-full">
                                                                            {dep.stereo}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                            </div>
                                        </Card>
                                    </div>
                                </>
                            )}

                        <div className="w-full h-px bg-border"></div>
                        <div>
                            <div className="text-xs font-semibold text-text-500 uppercase tracking-wider mb-3">
                                {t('serviceDetail.paymentInfo')}
                            </div>
                            <Card className="bg-box p-4 flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                    <div className="text-sub-title">{t('price.service.initial_setup_fee')}</div>
                                    <div className="font-medium">
                                        {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                        {product.price$?.initPrice?.toLocaleString() || 0}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-sub-title">{t('price.service.traffic_cost')}</div>
                                    <div className="font-medium">
                                        {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                        {product.price$?.trafficPrice?.toLocaleString() || 0}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-sub-title">{t('price.service.cost_per_hour')}</div>
                                    <div className="font-medium">
                                        {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                        {product.price$?.hourPerPrice?.toLocaleString() || 0}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between">
                                    <div className="text-sub-title">{t('price.service.per_call')}</div>
                                    <div className="font-medium">
                                        {getCurrencySymbol(product.catalog$?.price$?.currency)}
                                        {product.price$?.callPerPrice?.toLocaleString() || 0}
                                    </div>
                                </div>
                            </Card>
                        </div>

                        <div className="w-full h-px bg-border"></div>
                        <div>
                            <div className="text-xs font-semibold text-text-500 uppercase tracking-wider mb-3">
                                {t('serviceDetail.additionalInfo')}
                            </div>
                            <div>
                                <Label variant="form">{t('serviceDetail.memo')}</Label>
                                <Input
                                    placeholder={t('serviceDetail.memoPlaceholder')}
                                    className="bg-box"
                                    disabled
                                    value={product.memo}
                                />
                            </div>
                        </div>

                        {product.media$$ && product.media$$.length > 0 && (
                            <>
                                <div className="w-full h-px bg-border"></div>
                                <div>
                                    <Label className="p-0 text-base font-semibold">
                                        {t('serviceDetail.attachFile')}
                                    </Label>
                                    <div className="overflow-auto">
                                        <div className="flex items-center gap-x-2 overflow-auto">
                                            {product.media$$?.map(media =>
                                                media.contentType?.includes('pdf') ? (
                                                    <PdfItem key={media.id} fileUrl={media.url} />
                                                ) : (
                                                    <ImageItem key={media.id} fileUrl={media.url} />
                                                )
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </PageContainer>

            <ImageViewDialog url={fileUrl} open={imgViewerOpen} setOpen={setImageViewerOpen} />
            <PdfViewDialog url={fileUrl} open={pdfViewerOpen} setOpen={setPdfViewerOpen} />
        </>
    );
};
